import { useState } from "react"
import { RecurringDonation } from "../types"
import RecurringDonationDetails from "./RecurringDonationDetails"
import { PaymentMethodsProvider } from "../hooks/usePaymentMethodsContext"
import { Heading } from "@planningcenter/doxy-web"
import { Link } from "react-router-dom"
import { fetchRecurringDonations } from "../queries"
import { useSuspenseInfiniteQuery } from "source/registrations/utils/queryCache"
import { AlertMessage } from "source/shared/components/AlertMessage"
import InfiniteScroll from "react-infinite-scroller"
import { Loading } from "source/giving/donate/form/components/Loading"

interface RecurringDonationsListProps {
  showFullDetails: boolean
}

export default function RecurringDonationsList({
  showFullDetails,
}: RecurringDonationsListProps) {
  const [isEditingId, setIsEditingId] = useState<string | null>(null)

  const { data, isError, fetchNextPage, hasNextPage, isFetching } =
    useSuspenseInfiniteQuery({
      queryKey: ["RecurringDonations"],
      queryFn: ({ pageParam }) => {
        return fetchRecurringDonations({
          pageParam,
        })
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.meta?.next?.offset,
    })

  const donations: RecurringDonation[] =
    data.pages.flatMap((page) =>
      page.recurringDonations.filter((donation) =>
        !showFullDetails ? donation.on_hold_indefinitely === false : true,
      ),
    ) || []
  const loadMore = () => !isFetching && fetchNextPage()

  function handleEdit(id: string | null) {
    setIsEditingId(id)
  }

  if (isError) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  return (
    <>
      {donations.length === 0 ? (
        <div className="d-f fd-c action-drawer ta-c py-3 px-4 g-1">
          <div className="d-f jc-c">
            <img src="/static/recurring-gift.svg" alt="" width="32" />
          </div>
          <Heading
            level={3}
            size={3}
            color="tint1"
            text="No recurring gifts yet"
          />
          <p className="fw-500 c-tint2 mb-0">
            Consistent giving helps our church to plan confidently for ministry
            and outreach. Every faithful gift makes an impact.
          </p>
          <div>
            <Link className="btn md-btn" to="/giving?recurring=true">
              Schedule a recurring gift
            </Link>
          </div>
        </div>
      ) : (
        <div className="d-f fd-c g-1">
          {!showFullDetails && (
            <div className="d-f fd-c g-1">
              <span className="fw-600 fs-13 c-tint2 tt-u">
                Next recurring donation
              </span>
            </div>
          )}
          <PaymentMethodsProvider>
            <InfiniteScroll
              hasMore={hasNextPage}
              loadMore={loadMore}
              loader={<Loading key="loading" />}
              className="d-f fd-c g-2"
            >
              {donations.map((donation: RecurringDonation) => (
                <RecurringDonationDetails
                  key={donation.id}
                  donation={donation}
                  showFullDetails={showFullDetails}
                  onEdit={handleEdit}
                  isDisabled={isEditingId ? isEditingId !== donation.id : false}
                />
              ))}
            </InfiniteScroll>
          </PaymentMethodsProvider>
        </div>
      )}
    </>
  )
}
